import React, { useEffect, useRef, useState } from 'react';
import { axiosClient } from 'api/api';
import { v4 as uuid } from 'uuid';
import { getQueryConversation, sendQueryMessage, QueryMessage, UserQuery } from './UserQueries.api';
import { FiAlertCircle, FiCheck, FiClock, FiImage, FiLifeBuoy, FiPaperclip } from 'react-icons/fi';
import { dateLabel, initials } from './UserQueries.helpers';
import { isFinalStatus, normalizeStatus } from './TicketDetails';
import './TicketChat.css';

/* Email delivery of a support comment, shown as a compact marker in the comment header. */
const delivery = {
  sent: { icon: <FiCheck aria-hidden="true" />, label: 'Emailed', title: 'Sent by email', tone: 'is-sent' },
  sending: { icon: <FiClock aria-hidden="true" />, label: 'Sending', title: 'Email delivery pending', tone: 'is-pending' },
  review: { icon: <FiAlertCircle aria-hidden="true" />, label: 'Email review', title: 'Message saved; email delivery needs review', tone: 'is-review' },
};
/* One uploaded screenshot: a thumbnail that opens full size, or a plain link if the image fails to load. */
function Attachment({ url, index }: { url: string; index: number }) {
  const [broken, setBroken] = useState(false);
  return <a className={`uq-comment-file ${broken ? 'is-broken' : ''}`} href={url} target="_blank" rel="noopener noreferrer" title="Open full size" aria-label={`Open attachment ${index + 1} full size`}>
    {broken ? <><FiImage aria-hidden="true" /><span>Attachment {index + 1}</span></>
      : <img src={url} alt={`Attachment ${index + 1}`} loading="lazy" onError={() => setBroken(true)} />}
  </a>;
}

type CommentProps = { role: 'admin' | 'user'; avatar: React.ReactNode; author: string; time: string; body: string; tag?: string; emailState?: string; attachments?: string[] };
function Comment({ role, avatar, author, time, body, tag, emailState, attachments = [] }: CommentProps) {
  const email = role === 'admin' ? delivery[emailState as keyof typeof delivery] ?? delivery.review : null;
  return <article className={`uq-comment ${role === 'admin' ? 'is-support' : ''}`}>
    <span className="uq-comment-avatar" aria-hidden="true">{avatar}</span>
    <div className="uq-comment-card">
      <header className="uq-comment-head">
        <strong>{author}</strong>
        <span className="uq-comment-role">{role === 'admin' ? 'Support team' : 'Member'}</span>
        {tag && <span className="uq-comment-tag">{tag}</span>}
        <span className="uq-comment-aside">
          {email && <span className={`uq-comment-email ${email.tone}`} title={email.title}>{email.icon}{email.label}</span>}
          <time>{time}</time>
        </span>
      </header>
      <p className="uq-comment-body">{body}</p>
      {attachments.length > 0 && <div className="uq-comment-files">
        <span className="uq-comment-files-label"><FiPaperclip aria-hidden="true" />{attachments.length} attachment{attachments.length === 1 ? '' : 's'}</span>
        <div className="uq-comment-files-grid">{attachments.map((url, index) => <Attachment key={url + index} url={url} index={index} />)}</div>
      </div>}
    </div>
  </article>;
}

type Props = { ticket: UserQuery; admin?: boolean; disabled?: boolean; onUpdate: (ticket: UserQuery) => void; onBusy: (busy: boolean) => void };
export function TicketChat({ ticket, admin = false, disabled = false, onUpdate, onBusy }: Props) {
  const [messages, setMessages] = useState<QueryMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(false);
  const busy = useRef(false);
  const version = useRef(0);
  const mounted = useRef(true);
  const request = useRef<{ id: string; body: string } | null>(null);
  const callbacks = useRef({ onUpdate, onBusy });
  const externallyBusy = useRef(disabled);
  if (externallyBusy.current !== disabled) ++version.current;
  externallyBusy.current = disabled;
  callbacks.current = { onUpdate, onBusy };
  const read = useRef<() => Promise<void>>();
  const loadedFor = useRef('');
  const log = useRef<HTMLDivElement>(null);
  // Keep the newest message in view inside the scrollable log.
  useEffect(() => { if (log.current) log.current.scrollTop = log.current.scrollHeight; }, [messages.length, loaded]);
  useEffect(() => {
    const key = `${ticket.source}-${ticket.id}`;
    if (loadedFor.current !== key) return;
    const incoming = messages.filter(message => message.sender_role === (admin ? 'user' : 'admin'));
    const messageId = Math.max(0, ...incoming.map(message => message.id));
    if (!messageId) return;
    let pending = false;
    let done = false;
    let active = true;
    const markRead = async () => {
      if (document.hidden || pending || done) return;
      pending = true;
      try {
        await axiosClient.post(`/api/${admin ? 'admin/user-queries' : 'my-queries'}/${ticket.source}/${ticket.id}/read`, { messageId });
        done = true;
        if (active) window.dispatchEvent(new Event('support-messages-read'));
      } catch { /* Preserve unread state; the next conversation poll retries. */ }
      finally { pending = false; }
    };
    void markRead();
    document.addEventListener('visibilitychange', markRead);
    return () => { active = false; document.removeEventListener('visibilitychange', markRead); };
  }, [messages, ticket.source, ticket.id, admin]);
  useEffect(() => {
    mounted.current = true;
    let reading = false;
    const load = async () => {
      if (busy.current || externallyBusy.current || reading) return;
      reading = true;
      const current = ++version.current;
      try {
        const data = await getQueryConversation(ticket, admin);
        if (mounted.current && current === version.current) {
          loadedFor.current = `${ticket.source}-${ticket.id}`;
          setMessages(data.messages || []); setLoaded(true);
          callbacks.current.onUpdate(data);
        }
      } catch (err: any) {
        if (mounted.current && current === version.current) setError(err.response?.data?.message || 'Unable to load the conversation. Please refresh.');
      } finally { reading = false; }
    };
    read.current = load;
    void load();
    const timer = window.setInterval(() => { if (!document.hidden) void load(); }, 10000);
    return () => { mounted.current = false; ++version.current; window.clearInterval(timer); };
    // The parent updates the same ticket after each fetch; do not reset the draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket.id, ticket.source, admin]);
  // Resolved is final for everyone. Members can write only while In Progress; support's first
  // reply on an Open ticket is what moves it to In Progress.
  const resolved = isFinalStatus(ticket.ticket_status);
  const locked = resolved || (!admin && normalizeStatus(ticket.ticket_status) !== 'In Progress');
  const deliveryBlocked = ticket.reply_state === 'sending' || (admin && ticket.reply_state === 'review');
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy.current || disabled || locked || !draft.trim() || (!retry && deliveryBlocked)) return;
    if (!request.current) request.current = { id: uuid(), body: draft.trim() };
    busy.current = true; ++version.current; setSending(true); callbacks.current.onBusy(true); setError('');
    try {
      const data = await sendQueryMessage(ticket, request.current.body, request.current.id, admin);
      if (!mounted.current) return;
      setMessages(data.messages || []); callbacks.current.onUpdate(data);
      setDraft(''); setRetry(false); request.current = null;
    } catch (err: any) {
      if (!mounted.current) return;
      setError(err.response?.data?.message || 'Unable to confirm your message. Retry to check it safely.');
      if (!err.response || err.response.status >= 500) setRetry(true);
      else { setRetry(false); request.current = null; }
    } finally {
      busy.current = false;
      if (mounted.current) { setSending(false); callbacks.current.onBusy(false); void read.current?.(); }
    }
  };
  const memberAvatar = initials(ticket.user_name);
  const author = (role: 'admin' | 'user') => role === 'admin' ? 'Zamplia Support' : admin ? ticket.user_name || 'Member' : 'You';
  return <div className="uq-chat">
    <div className="uq-chat-head"><h3>Conversation</h3><button type="button" className="zs-btn zs-btn-ghost zs-btn-sm" disabled={sending} onClick={() => { setError(''); void read.current?.(); }}>Refresh</button></div>
    {error && <p role="alert" className="uq-alert uq-alert-danger mb-3">{error}</p>}
    {!loaded && <p role="status" className="uq-chat-loading">Loading conversation…</p>}
    {/* Comment-style thread: one full-width entry per message, oldest first, like a ticket activity log. */}
    <div ref={log} className="uq-chat-log" role="log" aria-label="Ticket messages" aria-live="polite">
      <Comment role="user" avatar={memberAvatar} author={author('user')} tag="Original query" time={dateLabel(ticket.created_at)} body={ticket.message} attachments={ticket.attachments} />
      {messages.map(message => <Comment key={message.id} role={message.sender_role} avatar={message.sender_role === 'admin' ? <FiLifeBuoy /> : memberAvatar} author={author(message.sender_role)} time={dateLabel(message.created_at)} body={message.body} emailState={message.email_state} />)}
    </div>
    {locked ? <p className="uq-alert uq-alert-info mt-4">{resolved
      ? 'This query is resolved. The conversation is closed and no new messages can be added.'
      : 'Our support team will reply soon. You can add messages once your query is in progress.'}</p> : <form className="uq-compose" onSubmit={submit}>
      <label className="zs-label" htmlFor={admin ? 'admin-chat-reply' : 'user-chat-reply'}>Your reply</label>
      {admin && <p className="zs-hint">Your message will also be emailed to the user. Resolve the ticket when the conversation is complete.</p>}
      <textarea id={admin ? 'admin-chat-reply' : 'user-chat-reply'} className="zs-textarea" rows={3} required maxLength={10000} value={draft} disabled={!loaded || disabled || sending || retry} onChange={event => setDraft(event.target.value)} />
      {deliveryBlocked && <p role="status" className="zs-hint">{ticket.reply_state === 'review' ? 'Email delivery needs review before support can send another message.' : 'A support email is being sent. Please wait.'}</p>}
      <div className="uq-compose-actions"><button className="zs-btn zs-btn-primary zs-btn-sm" type="submit" disabled={!loaded || disabled || sending || !draft.trim() || (!retry && deliveryBlocked)}>{sending ? 'Sending…' : retry ? 'Retry message' : 'Send reply'}</button></div>
    </form>}
  </div>;
}
